import { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useToast } from '../../../shared/components/Toast';
import { useAtualizarCustodian } from '../hooks';
import type { Custodian } from '../api';
import { TextInput } from '../../../shared/components/TextInput';
import { maskPhone } from '../../../shared/utils/validators';

interface Props {
  open: boolean;
  custodian: Custodian | null;
  onClose: () => void;
  onSaved: (custodian: Custodian) => void;
}

/**
 * Edita metadados de contato do custodiário: e-mail, telefone, cargo.
 *
 * `Tipo` e `Usuário do Nexos` ficam read-only propositalmente — o vínculo
 * user↔tipo é a identidade do custodiário (constraint UNIQUE). Se estiver
 * errado, o admin desativa e cria novo.
 */
export function CustodianEditModal({ open, custodian, onClose, onSaved }: Props) {
  const mutation = useAtualizarCustodian();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  useEffect(() => {
    if (custodian) {
      setEmail(custodian.email ?? '');
      setPhone(custodian.phone ?? '');
      setJobTitle(custodian.jobTitle ?? '');
    }
  }, [custodian]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!custodian) return;
    try {
      const saved = await mutation.mutateAsync({
        id: custodian.id,
        payload: {
          email: email.trim(),
          phone: phone.trim(),
          jobTitle: jobTitle.trim(),
        },
      });
      toast.success(`Custodiário "${saved.name}" atualizado.`);
      onSaved(saved);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao salvar alterações'));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Editar custodiário</Dialog.Title>

          {custodian && (
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <div>
                <TextInput
                  label="Tipo"
                  value={custodian.type}
                  disabled
                />
                <p className="mt-1 text-xs text-gray-500">
                  Tipo e Usuário não podem ser alterados. Se estiverem errados, desative e crie novo.
                </p>
              </div>

              <TextInput
                label="Usuário"
                value={custodian.name}
                disabled
              />

              <div className="grid grid-cols-2 gap-2">
                <TextInput
                  label="E-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextInput
                  label="Telefone"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                />
              </div>

              <TextInput
                label="Cargo/função"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {mutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
